import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { API } from "@/lib/api";

const SiteContext = createContext(null);

function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  return `${Math.round((h + 360) % 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function buildCategoryTree(categories) {
  const parents = categories.filter((c) => !c.parent_id);
  return parents.map((p) => ({
    ...p,
    children: categories.filter((c) => c.parent_id === p.id).sort((a, b) => a.order - b.order),
  }));
}

export function SiteProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([API.get("/settings/public"), API.get("/categories")]);
      setSettings(s.data);
      setCategories(c.data);
      const accent = s.data?.branding?.accent_color;
      if (accent) {
        const hsl = hexToHsl(accent);
        if (hsl) document.documentElement.style.setProperty("--primary", hsl);
      }
      const secondary = s.data?.branding?.secondary_color;
      if (secondary) {
        const hsl = hexToHsl(secondary);
        if (hsl) document.documentElement.style.setProperty("--secondary", hsl);
      }
    } catch (e) {
      // keep defaults on failure
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = {
    settings,
    categories,
    categoryTree: buildCategoryTree(categories),
    loading,
    refresh,
    storeOpen: settings ? settings.store_open !== false : true,
  };
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);

export function usePageContent(page) {
  const [content, setContent] = useState(null);
  useEffect(() => {
    let mounted = true;
    API.get(`/content/${page}`)
      .then((r) => mounted && setContent(r.data))
      .catch(() => mounted && setContent({}));
    return () => {
      mounted = false;
    };
  }, [page]);
  return content;
}
